import LogoIcon from '@/app/components/common/LogoIcon';
import { PortableText } from '@/app/components/questionnaireV2/components/portable-text/portable-text';
import AuthNavigationButton from '@/components/decision-tree/components/SurveyItem/components/Question/questionTypes/EmailAndName/AuthNavigationButton';
import { HeroVideo } from '@/components/hero-video/hero-video';
import { ROUTE_SIGN_IN, ROUTE_SIGN_UP } from '@/constants/routes';
import useVisitorSession from '@/hooks/useVisitorSession';
import authService from '@/services/auth.service';
import { getRoute, isValidEmail } from '@/utils';
import { motion } from 'motion/react';
import { useCallback, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FRAME_TRANSITION_DURATION } from '../../../interstitial/interstitial-shared';
import { EmailOnlyQuestionProps } from '../../types';
import { EmailInputBase } from '../components/email-input-base';

export const EmailOnlyQuestion = ({ question }: EmailOnlyQuestionProps) => {
  const { control, setError, clearErrors } = useFormContext();
  const { captureLeadAndName } = useVisitorSession();
  const lastCapturedRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailCapture = useCallback(
    async (email: string) => {
      // Skip if email is exactly the same as last capture
      if (lastCapturedRef.current === email) {
        return;
      }

      try {
        // Get invite code from localStorage if present
        const inviteCode = authService.getInviteCode();

        // Capture email as lead
        await captureLeadAndName({
          email,
          inviteCode: inviteCode || undefined,
        });

        // Update last captured value
        lastCapturedRef.current = email;
      } catch (err: any) {
        const status = err?.response?.status || err?.status;

        // Handle different error types appropriately
        if (status === 400 || status === 401) {
          // 400/401: Expected errors - silently ignore
          return;
        } else if (status >= 500) {
          // 5xx: Server errors - log as errors
          console.error('Lead capture server error:', err);
        } else {
          // Other errors (network issues, etc.)
          console.error('Failed to capture email:', err);
        }
      }
    },
    [captureLeadAndName],
  );

  const handleGetStarted = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get the email value from form
      const values = control._formValues;
      const email = values[question.slug];

      // Validate email
      const hasContent = email?.trim();

      if (question.required || hasContent) {
        // Email must be filled
        if (!email?.trim()) {
          setError(question.slug, {
            type: 'required',
            message: 'Email is required',
          });
          setIsLoading(false);
          return;
        }

        // Email format validation using shared utility
        if (!isValidEmail(email)) {
          setError(question.slug, {
            type: 'validation',
            message: 'Please enter a valid email address',
          });
          setIsLoading(false);
          return;
        }
      }

      // Clear any existing errors
      clearErrors(question.slug);

      if (email) {
        await handleEmailCapture(email);

        // Only dispatch event after successful validation and capture
        const event = new CustomEvent('email-captured', {
          detail: { email },
          bubbles: true,
        });
        document.dispatchEvent(event);
      }
    } catch (error) {
      console.error('Error capturing email:', error);
    } finally {
      setIsLoading(false);
    }
  }, [control, question.slug, question.required, handleEmailCapture, setError, clearErrors]);

  return (
    <Controller
      control={control}
      name={question.slug}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const currentValue = value || '';

        const handleEmailChange = (newEmail: string) => {
          onChange(newEmail);
        };

        return (
          <motion.div
            className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-black font-['Inter']"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: FRAME_TRANSITION_DURATION }}
          >
            {/* Video Section - Top Half */}
            <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden">
              {/* Hero Video Background */}
              <HeroVideo src="/videos/catch-hero.mp4">
                <div className="flex max-w-[25rem] flex-col items-center justify-center gap-2 px-4">
                  <LogoIcon color="white" width="43" height="43" />
                  <h1 className="text-center text-2xl leading-[130%] font-normal text-white md:text-3xl">
                    {question.subtitle ? (
                      <PortableText content={question.subtitle} inline />
                    ) : (
                      'Preventing Cancer Starts with Knowing Your Risk'
                    )}
                  </h1>
                </div>
              </HeroVideo>
            </div>

            {/* White Drawer Section - Bottom */}
            <div className="pb-safe-offset-8 relative z-10 mt-[-3rem] flex w-full flex-col items-center rounded-t-[2rem] bg-gradient-to-b from-white to-[#F5F5F7] pt-8">
              <div className="flex w-full max-w-[30rem] flex-col items-center px-6 md:px-12">
                {/* Sign Up / Login Toggle */}
                <div className="mb-8 flex w-full flex-row justify-between gap-2 rounded-full bg-[#EAECF0] p-1.5">
                  <div className="flex-1">
                    <AuthNavigationButton to={getRoute(ROUTE_SIGN_UP)} active disabled>
                      Sign Up
                    </AuthNavigationButton>
                  </div>
                  <div className="flex-1">
                    <AuthNavigationButton to={getRoute(ROUTE_SIGN_IN)}>Login</AuthNavigationButton>
                  </div>
                </div>

                {/* Email Input Field */}
                <div className="mb-8 w-full">
                  <div style={{ width: '100%' }}>
                    <style>{`
                      .email-only-input .input-field-container {
                        width: 100%;
                      }
                    `}</style>
                    <div className="email-only-input">
                      <EmailInputBase
                        value={currentValue}
                        onChange={handleEmailChange}
                        placeholder="Email Address"
                        error={error?.message}
                      />
                    </div>
                  </div>
                  <div className={`mt-2 text-left text-sm text-red-500 ${!error?.message ? 'invisible' : ''} `}>
                    {error?.message || ''}
                  </div>
                </div>

                {/* Get Started Button */}
                <div className="flex w-full justify-center">
                  <button
                    type="button"
                    onClick={handleGetStarted}
                    disabled={isLoading}
                    className="flex w-full items-center justify-center rounded-full bg-[#9EFFC1] px-10 py-5 text-xl font-semibold text-[#0F2837] transition-all hover:bg-[#8AEDB0] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isLoading ? 'Loading...' : 'Get Started'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        );
      }}
      defaultValue=""
    />
  );
};
